import { Link } from 'react-router-dom';
import { resolveKeycloakClientRoleFromNames } from '../../auth/clientRoles';
import { useAuth } from '../../auth/useAuth';
import { useTenantUserRolesQuery } from '../../features/tenant/queries/useTenantUserRolesQuery';
import { TenantUserListPhoto } from '../../features/user/components/UserListPhoto';
import { formatInitials } from '../../features/user/userDisplay';
import { useI18n } from '../../i18n/useI18n';
import { resolveTenantUserPath } from '../../routes/tenantPaths';
import { formatUserDisplayName, type TenantUser } from './tenantUsersTabUtils';

export function TenantUserRow({ user }: { user: TenantUser }) {
  const { translate } = useI18n();
  const { user: authUser } = useAuth();
  const rolesQuery = useTenantUserRolesQuery(user.id);
  const personName = formatUserDisplayName(
    user,
    translate('organisationManagement.users.unknownUser'),
  );
  const username = user.username?.trim() ?? '';
  const showUsername = username !== '' && personName !== username;
  const isCurrentUser = Boolean(authUser?.id && user.id === authUser.id);
  const initials = formatInitials(personName);

  const role = (() => {
    if (rolesQuery.isLoading || rolesQuery.isError || !rolesQuery.data) {
      return null;
    }
    return resolveKeycloakClientRoleFromNames(rolesQuery.data);
  })();

  const roleLabel = (() => {
    if (rolesQuery.isLoading) {
      return translate('organisationManagement.users.roleLoading');
    }
    if (role == null) {
      return translate('organisationManagement.users.roleUnknown');
    }
    return translate(`organisationManagement.users.roles.${role}`);
  })();

  return (
    <li className="tenant-users__user-item">
      <Link
        to={resolveTenantUserPath(user.id)}
        className="tenant-users__user"
        aria-label={translate('organisationManagement.users.openProfile', {
          name: personName,
        })}
      >
        <TenantUserListPhoto
          thumbnail={user.thumbnail}
          initials={initials}
          className="tenant-users__photo"
        />
        <div className="tenant-users__user-person">
          <span className="tenant-users__user-name">
            <span className="tenant-users__user-name-text">{personName}</span>
            {isCurrentUser && (
              <span className="tenant-users__you-badge">
                {translate('common.youBadge')}
              </span>
            )}
          </span>
          {showUsername && (
            <span className="tenant-users__user-username">{username}</span>
          )}
        </div>
        <span className="tenant-users__user-email">
          {user.email?.trim() ||
            translate('organisationManagement.users.noEmail')}
        </span>
        <span className="tenant-users__user-role">
          <span
            className={[
              'tenant-users__badge',
              'tenant-users__badge--role',
              role === 'ADMIN' && 'tenant-users__badge--role-admin',
              role === 'LAC_ADMIN' && 'tenant-users__badge--role-admin',
              role === 'MEMBER' && 'tenant-users__badge--role-member',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {roleLabel}
          </span>
        </span>
        <span className="tenant-users__user-status">
          {user.enabled == null ? (
            translate('organisationManagement.users.statusUnknown')
          ) : (
            <span
              className={`tenant-users__badge${
                user.enabled ? '' : ' tenant-users__badge--disabled'
              }`}
            >
              {user.enabled
                ? translate('organisationManagement.users.enabled')
                : translate('organisationManagement.users.disabled')}
            </span>
          )}
        </span>
      </Link>
    </li>
  );
}
